import React from 'react'
import CssBaseline from '@mui/material/CssBaseline'

import '@fontsource/inter'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'

import {
  createTheme,
  SxProps as OriginalSxProps,
  PaletteColor,
  StyledEngineProvider,
  ThemeProvider,
} from '@mui/material/styles'

import env from '@core/env'

import { colorPalette } from './color-palette'
import {
  background,
  common,
  fgProgress,
  histogram,
  linkBlue,
  other,
  palette,
  states,
  suggestAnswer,
  tag,
  text,
} from './colors'

interface CustomPalette {
  tertiary: PaletteColor
  copyOption: PaletteColor
  purple: PaletteColor
  purpleLight: PaletteColor
  danger: PaletteColor
  primaryGenerate: PaletteColor
  secondaryGenerate: PaletteColor
  color: typeof colorPalette
  other: typeof other
  tag: typeof tag
  suggestAnswer: typeof suggestAnswer
}

type CustomBackground = typeof background

declare module '@mui/material/styles' {
  interface Palette extends CustomPalette {}
  interface PaletteOptions extends Partial<CustomPalette> {}
  // Custom colors live on MUI's reserved `text` key. Augment TypeText (not the
  // `text` property type) so it stays compatible with MUI's Palette/PaletteOptions.
  interface TypeText extends Record<keyof typeof text, string> {}
  interface TypeBackground extends CustomBackground {}
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true
    copyOption: true
    purple: true
    purpleLight: true
    primaryGenerate: true
    secondaryGenerate: true
    danger: true
    successLight: true
  }
}

declare module '@mui/material/Badge' {
  interface BadgePropsColorOverrides {
    tertiary: true
    copyOption: true
    purple: true
    purpleLight: true
    primaryGenerate: true
    danger: true
    successLight: true
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    tertiary: true
    copyOption: true
    purple: true
    purpleLight: true
    danger: true
    primaryGenerate: true
    successLight: true
  }
}

declare module '@mui/material/CircularProgress' {
  interface CircularProgressPropsColorOverrides {
    tertiary: true
    copyOption: true
    purple: true
    purpleLight: true
    primaryGenerate: true
    danger: true
    successLight: true
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    tertiary: true
    copyOption: true
    purple: true
    purpleLight: true
    primaryGenerate: true
    danger: true
    successLight: true
  }
}

declare module '@mui/material/Dialog' {
  interface DialogProps {
    'data-full-page-width'?: boolean
    'data-full-page-height'?: boolean
  }
}

const defaultTheme = createTheme()

const customScroll = (selector: string) => {
  return {
    [selector]: {
      scrollbarWidth: 'thin',
    },
  }
}

const tagColors = {}

for (const [name, value] of Object.entries(tag)) {
  tagColors[name] = defaultTheme.palette.augmentColor({
    name,
    color: {
      // @ts-ignore
      main: value.main,
      contrastText: value.contrastText,
    },
  })
}

const theme = createTheme({
  palette: {
    primary: { main: text.slateBlue, contrastText: common.white },
    secondary: {
      main: fgProgress,
      contrastText: common.white,
    },
    purple: defaultTheme.palette.augmentColor({
      color: { main: background.purple },
      name: 'purple',
    }),
    tertiary: defaultTheme.palette.augmentColor({
      color: {
        main: '#f5f7f8',
        dark: '#ecedf0',
        contrastText: '#42526e',
      },
      name: 'tertiary',
    }),
    copyOption: defaultTheme.palette.augmentColor({
      color: {
        main: '#DDEBFF',
        dark: '#DDEBFF',
        contrastText: '#2685FF',
      },
      name: 'copyOption',
    }),
    success: defaultTheme.palette.augmentColor({
      color: {
        main: states.success,
      },
      name: 'success',
    }),
    successLight: defaultTheme.palette.augmentColor({
      color: {
        main: '#e4fbec',
        contrastText: '#42526e',
      },
      name: 'successLight',
    }),
    error: defaultTheme.palette.augmentColor({
      color: {
        main: states.error,
      },
      name: 'error',
    }),
    warning: defaultTheme.palette.augmentColor({
      color: {
        main: states.warning,
      },
      name: 'warning',
    }),
    danger: defaultTheme.palette.augmentColor({
      color: {
        // @ts-ignore
        main: palette.red.R050,
        dark: palette.red.R075,
        contrastText: other.grayishBlueDark,
      },
    }),
    purpleLight: defaultTheme.palette.augmentColor({
      color: {
        // @ts-ignore
        main: palette.purple.P050,
        dark: palette.purple.P075,
        contrastText: other.grayishBlueDark,
      },
    }),
    primaryGenerate: defaultTheme.palette.augmentColor({
      color: {
        // @ts-ignore
        main: palette.blue.B200,
        dark: palette.blue.B300,
        contrastText: other.grayishBlueDark,
      },
    }),
    secondaryGenerate: defaultTheme.palette.augmentColor({
      color: {
        // @ts-ignore
        main: '#fff',
        dark: palette.neutral.N040,
        contrastText: palette.neutral.N400,
      },
    }),
    background: { default: background.grey, ...background, ...states },
    // @ts-ignore
    text: { primary: text.slateBlue, secondary: text.greyBlue, ...text, ...states, linkBlue },
    // @ts-ignore
    tag: tagColors,
    histogram,
    other,
    suggestAnswer,
    color: colorPalette,
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    h1: { fontSize: '2rem', fontWeight: 500 },
    h2: { fontSize: '1.75rem', fontWeight: 500 },
    h3: { fontSize: '1.5rem', fontWeight: 500 },
    h4: { fontSize: '1.25rem', fontWeight: 500 },
    h5: { fontSize: '1rem', fontWeight: 500 },
    h6: { fontSize: '1rem', fontWeight: 500 },
    button: {
      textTransform: 'initial',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '1rem',
          lineHeight: 1.625,
          fontWeight: 400,
          letterSpacing: '-0.13px',
          ...customScroll('&'),
          ...customScroll('*'),
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '3px',
        },
        contained: {
          '&.Mui-disabled': {
            backgroundColor: background.disabledButton,
          },
        },
        containedSecondary: {
          backgroundColor: linkBlue,
        },
        outlinedSecondary: {
          borderColor: linkBlue,
          color: linkBlue,
        },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'tertiary' },
          style: {
            '&.Mui-focusVisible': {
              outline: `2px solid ${palette.blue.B200}`,
              background: palette.neutral.N040,
            },
          },
        },
        {
          props: { variant: 'contained', color: 'danger' },
          style: {
            '&.Mui-focusVisible': {
              outline: `2px solid ${palette.red.R200}`,
              background: palette.red.R075,
            },
          },
        },
        {
          props: { variant: 'contained', color: 'purpleLight' },
          style: {
            '&.Mui-focusVisible': {
              outline: `2px solid ${palette.blue.B200}`,
              background: palette.purple.P100,
            },
          },
        },
        {
          props: { variant: 'contained', color: 'successLight' },
          style: {
            '&.Mui-focusVisible': {
              outline: `2px solid ${palette.blue.B200}`,
              background: '#5ae78b',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'primaryGenerate' },
          style: {
            background: `${palette.blue.B200}`,
            padding: '8px 16px',
            color: '#fff',
            border: '1px solid #fff',
            borderRadius: '6px',
            '&.Mui-focusVisible': {
              outline: `1px solid ${palette.blue.B200}`,
              border: `1px solid ${palette.blue.B200}`,
            },
            '&:hover': {
              border: `1px solid ${palette.blue.B200}`,
              background: `${palette.blue.B200}`,
            },
            '&.Mui-disabled': {
              background: `${palette.blue.B200}`,
              opacity: 0.5,
              color: '#fff',
              cursor: 'not-allowed',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'secondaryGenerate' },
          style: {
            padding: '8px 16px',
            border: `1px solid ${palette.neutral.N050}`,
            borderRadius: '6px',
            '&:hover': {
              outline: `2px solid ${palette.neutral.N050}`,
              border: `1px solid ${palette.neutral.N050}`,
              background: palette.neutral.N010,
            },
            '&.Mui-focusVisible': {
              outline: `2px solid ${palette.blue.B200}`,
              border: `1px solid ${palette.blue.B200}`,
              background: '#fff',
            },

            '&.Mui-disabled': {
              borderColor: palette.neutral.N030,
            },
            '&:active': {
              outline: `2px solid ${palette.blue.B200}`,
              border: `1px solid ${palette.blue.B200}`,
              background: palette.neutral.N010,
            },
          },
        },
      ],
    },
    MuiInput: {
      defaultProps: {
        disableUnderline: true,
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        colorSuccess: {
          background: '#e2ffee',
          color: '#006644',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: text.slateBlue,
        },
      },
      defaultProps: {
        enterNextDelay: 500,
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: defaultTheme.spacing(0, 3, 3, 0),
        },
      },
    },
    MuiDialog: {
      variants: [
        {
          props: { 'data-full-page-width': true },
          style: {
            '& .MuiDialog-paper': {
              maxWidth: 'calc(1280px - 64px)',
              width: '100%',
            },
          },
        },
        {
          props: { 'data-full-page-height': true },
          style: {
            '& .MuiDialog-paper': {
              maxHeight: 'calc(100vh - 64px)',
              height: '100%',
            },
          },
        },
      ],
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: text.blue,
          height: 4,
          '&.Mui-disabled': {
            color: other.sliderDisabled,
            '& .MuiSlider-rail': {
              backgroundColor: other.sliderDisabled,
            },
            '& .MuiSlider-markLabel': {
              opacity: 0.5,
            },
          },
        },
        thumb: {
          height: 16,
          width: 16,
          backgroundColor: '#fff',
          border: `2px solid ${background.notification}`,
          '&:focus, &:hover, &$active': {
            boxShadow: 'inherit',
          },
          '&.Mui-disabled': {
            height: 16,
            width: 16,
            backgroundColor: other.sliderDisabled,
            border: `2px solid ${other.sliderDisabled}`,
          },
        },
        rail: { height: 4, borderRadius: 2, backgroundColor: other.ghostBlue, opacity: 1 },
        mark: { display: 'none' },
        markLabel: { fontWeight: 500, color: text.slateBlue },
        track: { height: 4, borderRadius: 2 },
      },
    },
    MuiAlert: {
      variants: [
        {
          props: { severity: 'warning', color: 'error' },
          style: {
            color: palette.red.R500,
          },
        },
      ],
    },
  },
})

if (env.ENV === 'local') {
  // @ts-ignore
  window.theme = theme
}

declare module '@mui/material' {
  /**
   * StyleProps Usage:
   * ```
   * import { StyleProps } from '@mui/material'
   *
   * const styles = {
   *   container: {
   *     bgcolor: 'background.paper',
   *     mx: 2,
   *   }
   * } satisfies StyleProps
   * ...
   * <Box sx={styles.container}>...</Box>
   * ```
   */
  type StyleProps = Record<string, OriginalSxProps<typeof theme>>
}

export const AppTheme = ({
  children,
  cssReset = true,
}: {
  children: React.ReactNode
  cssReset?: boolean
}) => {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        {cssReset && <CssBaseline />}
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default theme
